import {
  toCompareTogglePlayer,
  useCompareToggle,
} from "@/components/organisms/Players/MrDbListCardView/PlayersCardView";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Button as DsButton } from "@/design-system/Button/Button";
import { type DropdownItem } from "@/design-system/Dropdown/Dropdown";
import { SortDropdown } from "@/design-system/Dropdown/SortDropdown";
import {
  ExpandableSearch,
  useExpandableSearch,
} from "@/design-system/Input/ExpandableSearch";
import { Menu, type MenuItem } from "@/design-system/Menu/Menu";
import { MetaRatingExplainer } from "@/design-system/Tags/MetaRatingChip/MetaRatingExplainer";
import * as evolutionsMixpanel from "@/events/evolutionsMixpanel";
import * as playersMixpanel from "@/events/playersMixpanel";
import { EvolutionStatesInfo } from "@/features/Evolutions/EvolutionStatesInfo";
import {
  RangeFilterType,
  SelectorFilterType,
} from "@/pages/Players/FiltersPanel/filterTypes";
import { EvolutionBuilderPlayer } from "@/redux/evolutions/evolutionsBuilderSlice";
import type { PlayersSortByType } from "@repo/shared/api";
import {
  EVOLUTION_PLAYERS_SORT_OPTIONS,
  EVOS_BUILDER_SORT_OPTIONS,
  PLAYERS_PAGE_SORT_OPTIONS,
} from "@repo/shared/evolutions";
import { useTranslation } from "@repo/shared/i18n";
import { comparePlayersSelector } from "@repo/shared/redux";
import { Check, Dna, Info, Plus, Save, Search, Share } from "lucide-react";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useMediaQuery } from "usehooks-ts";

import { MetaRatingIcon } from "../../../icons";
import { filter as filterIcon } from "../../../icons";
import {
  PlayerRole,
  playerRoleLabelSelector,
} from "../../../redux/players/playersSlice";
import { BREAKPOINTS } from "../../../settings";
import { BaseDropdownOption } from "../../dropdowns/BaseDropdown";
import {
  ExcludedPlayStyleChips,
  FiltersRow,
} from "../Players/FlitersRow/FiltersRow";

type Props<T> = {
  searchTerm: string;
  setSearchTerm: (searchTerm: string) => void;
  handleToolbarAction: (action: T) => void;
  isMobile: boolean;
  hasFilters: boolean;
  sortBy?: PlayersSortByType;
  setSortBy?: (value: PlayersSortByType) => void;
  showPlayerRole?: boolean;
  // Single player role selection (used in players/evolutions pages)
  playerRole?: PlayerRole;
  // Multi-select player roles (used in evolution builder)
  selectedPlayerRoles?: PlayerRole[];
  pageType:
    | "players"
    | "evolutions"
    | "my-evolutions"
    | "evolutions-builder"
    | "playstyles-lab";
  /** Builder/lab: a save is in flight, the Save button shows a spinner. */
  saving?: boolean;
  /** Builder: the evolution player as currently built — enables the Compare toggle. */
  currentEvolutionPlayer?: EvolutionBuilderPlayer;
  isEvolutionsBuilderContext?: boolean;
  includedEvolutionsCount?: number;
  // Optional: custom active filters and handlers for FiltersRow
  activeFilters?: (SelectorFilterType | RangeFilterType)[];
  onClearFilter?: (filterKey: string) => void;
  onClearFilterOption?: (filterKey: string, option: number) => void;
  onClearAllFilters?: () => void;
  excludedPlayStyleChips?: ExcludedPlayStyleChips[];
  onClearExcludedPlayStyle?: (filterKey: string, id: number) => void;
  /** One-tap filters (or tabs) beside the sort. */
  quickFilters?: React.ReactNode;
  /** A Compare button that switches the list's compare mode. */
  compareMode?: CompareModeControl;
  /** List/grid switch beside the sort. */
  viewToggle?: React.ReactNode;
};

/**
 * The builder's actions, hung off a kebab as the SBC set card does: Save,
 * Share and, on phones, Compare. Compare is a toggle against the
 * compare tray, so the row reports whether the current build is staged.
 */
const BuilderActionsMenu = ({
  player,
  saving,
  onSave,
  onShare,
  withCompare,
}: {
  player?: EvolutionBuilderPlayer;
  saving?: boolean;
  onSave: () => void;
  onShare?: () => void;
  withCompare: boolean;
}) => {
  const { t } = useTranslation("common");
  const compare = useCompareToggle(
    // A player is needed for the hook; without one the row is left out.
    toCompareTogglePlayer(player ?? ({} as EvolutionBuilderPlayer)),
    { section: "Evolutions", subsection: "Builder" },
  );
  const items: MenuItem[] = [
    {
      value: "save",
      label: t("save"),
      icon: <Save className="size-4" />,
      disabled: saving,
      onSelect: onSave,
    },
    ...(onShare
      ? [
          {
            value: "share",
            label: t("share"),
            icon: <Share className="size-4" />,
            onSelect: onShare,
          },
        ]
      : []),
    ...(player && withCompare
      ? [
          {
            value: "compare",
            label: t("compare"),
            icon: compare.isInCompare ? (
              <Check className="size-4" />
            ) : (
              <Plus className="size-4" />
            ),
            selected: compare.isInCompare,
            // The toggle guards against the row it usually sits on; the menu
            // already stopped the click, so a stand-in event is enough.
            onSelect: () =>
              compare.handleToggleCompare({
                preventDefault() {},
                stopPropagation() {},
              } as React.MouseEvent),
          },
        ]
      : []),
  ];
  return <Menu label={t("moreActions")} items={items} />;
};

/** Desktop's Compare, out of the kebab: a toggle against the compare tray. */
const CompareBuildButton = ({ player }: { player: EvolutionBuilderPlayer }) => {
  const { t } = useTranslation("common");
  const { isInCompare, handleToggleCompare } = useCompareToggle(
    toCompareTogglePlayer(player),
    { section: "Evolutions", subsection: "Builder" },
  );
  return (
    <DsButton
      variant="secondary"
      size="small"
      icon={
        isInCompare ? <Check className="size-4" /> : <Plus className="size-4" />
      }
      selected={isInCompare}
      onClick={handleToggleCompare}
    >
      {t("compare")}
    </DsButton>
  );
};

type CompareModeControl = { active: boolean; onToggle: () => void };

/** Switches compare mode on the list; counts the players already staged. */
const CompareModeButton = ({
  active,
  onToggle,
}: {
  active: boolean;
  onToggle: () => void;
}) => {
  const { t } = useTranslation("common");
  const staged = useSelector(comparePlayersSelector).length;
  return (
    <DsButton
      variant="secondary"
      size="small"
      selected={active}
      icon={active ? <Check className="size-4" /> : <Plus className="size-4" />}
      onClick={onToggle}
    >
      <span className="flex items-center gap-x-1">
        <span>{t("compare")}</span>
        {staged > 0 && (
          <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-control text-[10px] font-bold text-foreground-muted">
            {staged}
          </span>
        )}
      </span>
    </DsButton>
  );
};

/**
 * The toolbar on the 2.0 design system: the SBC set list's band, one to one.
 * Search on the left, the actions kebab on the right. The pickers (filters,
 * role, evolutions) sit on the right, except in the builder, where
 * above phone width they follow the search and Compare leaves the kebab
 * for its own button.
 */
const NewPlayersToolbar = <T extends string>({
  searchTerm,
  setSearchTerm,
  handleToolbarAction,
  isMobile,
  sortBy,
  setSortBy,
  sortOptions,
  currentSortOption,
  trackSort,
  pageType,
  showPlayerRole,
  rolesButtonLabel,
  pickedRoleLabel,
  selectedPlayerRoles,
  saving,
  currentEvolutionPlayer,
  isEvolutionsBuilderContext,
  includedEvolutionsCount,
  chips,
  quickFilters,
  compareMode,
  viewToggle,
}: {
  searchTerm: string;
  setSearchTerm: (searchTerm: string) => void;
  handleToolbarAction: (action: T) => void;
  isMobile: boolean;
  sortBy?: PlayersSortByType;
  setSortBy?: (value: PlayersSortByType) => void;
  sortOptions: BaseDropdownOption[];
  currentSortOption: BaseDropdownOption | null;
  trackSort: (value: string) => void;
  pageType: Props<T>["pageType"];
  showPlayerRole: boolean;
  rolesButtonLabel: string | null;
  /** The chosen role's label, when one is picked rather than Best. */
  pickedRoleLabel?: string;
  selectedPlayerRoles?: PlayerRole[];
  saving?: boolean;
  currentEvolutionPlayer?: EvolutionBuilderPlayer;
  isEvolutionsBuilderContext?: boolean;
  includedEvolutionsCount: number;
  /** Active filter chips, drawn left of the sort. */
  chips?: React.ReactNode;
  /** One-tap filters, left of the sort. */
  quickFilters?: React.ReactNode;
  compareMode?: CompareModeControl;
  /** List/grid switch, left of the sort. */
  viewToggle?: React.ReactNode;
}) => {
  const { t } = useTranslation(["players", "common"]);
  const isTouchDevice = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);
  // On a phone the search takes the band over from the pickers on the right.
  const search = useExpandableSearch(searchTerm, isMobile);
  const searchTakesOver = search.takesOver;
  const searchPlaceholder =
    pageType === "playstyles-lab"
      ? t("toolbar.searchPlayStyles")
      : pageType === "evolutions-builder"
        ? t("toolbar.searchEvolutions")
        : t("toolbar.searchPlayers");
  const isBuilder =
    pageType === "evolutions-builder" || pageType === "playstyles-lab";
  const showFilters =
    pageType !== "my-evolutions" && pageType !== "playstyles-lab";
  const sortItems: DropdownItem[] = sortOptions.map((option) => ({
    label: option.label,
    value: String(option.value),
  }));

  // Next to the search on phones, where it is one more round icon; first of
  // the pickers on desktop.
  const filtersButton = showFilters && !searchTakesOver && (
    <DsButton
      variant="secondary"
      size="small"
      icon={<div className="size-4">{filterIcon}</div>}
      aria-label={t("common:filters")}
      onClick={(e) => {
        e.stopPropagation();
        handleToolbarAction("filters" as T);
      }}
    >
      {/* undefined, not false: only a null child makes Button take the round
          icon-only skin the search button next to it has. */}
      {isTouchDevice ? undefined : <span>{t("common:filters")}</span>}
    </DsButton>
  );

  const roleButton = (!isEvolutionsBuilderContext ||
    pageType === "evolutions-builder") &&
    showPlayerRole && (
      <DsButton
        variant="secondary"
        size="small"
        icon={<MetaRatingIcon width="16" height="16" />}
        // The panels these open close on any outside click, and
        // the click that opens them counts unless it stops here.
        onClick={(e) => {
          e.stopPropagation();
          handleToolbarAction("playerRole" as T);
        }}
      >
        <span className="flex items-center gap-x-1 truncate max-mobile:max-w-20 mobile:max-touch-device:max-w-36">
          {selectedPlayerRoles && selectedPlayerRoles.length > 1 ? (
            <>
              <span>{t("toolbar.roles")}</span>
              <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-control text-[10px] font-bold text-foreground-muted">
                {selectedPlayerRoles.length}
              </span>
            </>
          ) : (
            <span>{rolesButtonLabel}</span>
          )}
        </span>
      </DsButton>
    );

  // The players hub: role beside the search, filters leading the quick
  // filters row, view toggle beside Compare.
  const hubLayout = Boolean(quickFilters);

  const evosButton = (pageType === "evolutions-builder" ||
    pageType === "evolutions") && (
    <DsButton
      variant="secondary"
      size="small"
      icon={<Dna className="size-4" />}
      onClick={(e) => {
        e.stopPropagation();
        handleToolbarAction("includeExclude" as T);
      }}
    >
      {/* One span: the button lays its children out as flex items. */}
      <span className="whitespace-nowrap">
        {/* "Evos" only where the band is short on room. */}
        <span className="mobile:hidden">
          {t("toolbar.evosCount", { count: includedEvolutionsCount })}
        </span>
        <span className="max-mobile:hidden">
          {t("toolbar.evolutionsCount", { count: includedEvolutionsCount })}
        </span>
      </span>
    </DsButton>
  );
  // Why only some evolutions are used, next to the button that picks them.
  const evosControl = evosButton && (
    <span className="flex shrink-0 items-center gap-x-2">
      {evosButton}
      <EvolutionStatesInfo />
    </span>
  );

  // Builder above phone width: the pickers follow the search instead of the kebab.
  const pickersBesideSearch = isBuilder && !isMobile;
  const pickers = (
    <>
      {!isTouchDevice && !hubLayout && filtersButton}
      {!hubLayout && roleButton}
      {hubLayout && viewToggle}
      {compareMode && !(hubLayout && isMobile) && (
        <CompareModeButton
          active={compareMode.active}
          onToggle={compareMode.onToggle}
        />
      )}
      {!hubLayout && evosControl}
    </>
  );

  const showSort = sortBy && setSortBy && pageType !== "evolutions-builder";
  // A picked role follows the sort, as on mobile: "Meta Rating · Winger ST".
  const sortName = currentSortOption?.label ?? t("common:sortBy");
  const sortLabel = pickedRoleLabel
    ? `${sortName} · ${pickedRoleLabel}`
    : sortName;

  return (
    <>
      <div className="flex h-15 w-full flex-row justify-center bg-surface py-2 touch-device:py-4">
        <div className="mx-auto flex w-full max-w-content items-center px-4 large:px-16 touch-device:px-4">
          <div className="flex h-full w-full flex-row items-center gap-x-4 max-touch-device:gap-x-2">
            <div className="flex flex-1 shrink-0 items-center gap-x-2">
              {/* A floor on tablet: a long role name beside it would
                  squeeze the field to nothing. */}
              <ExpandableSearch
                search={search}
                value={searchTerm}
                onChange={setSearchTerm}
                placeholder={searchPlaceholder}
                className="mobile:max-touch-device:min-w-40"
              />
              {isTouchDevice && !hubLayout && filtersButton}
              {hubLayout && !searchTakesOver && roleButton}
              {hubLayout && !searchTakesOver && evosControl}
              {pickersBesideSearch && pickers}
            </div>
            {!searchTakesOver && (
              <div className="flex flex-1 shrink-0 items-center justify-end gap-x-2 whitespace-nowrap text-sm">
                {!pickersBesideSearch && pickers}
                {isBuilder && (
                  <BuilderActionsMenu
                    player={currentEvolutionPlayer}
                    saving={saving}
                    onSave={() => handleToolbarAction("saveEvolution" as T)}
                    onShare={
                      isTouchDevice
                        ? undefined
                        : () => handleToolbarAction("downloadAssets" as T)
                    }
                    withCompare={!pickersBesideSearch}
                  />
                )}
                {pickersBesideSearch && currentEvolutionPlayer && (
                  <CompareBuildButton player={currentEvolutionPlayer} />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Active filter chips left, sort right: the SBC set list's row. The sort
          is stated, not labelled; the words are the control. */}
      {(showSort || quickFilters || viewToggle) && (
        <div className="mx-auto flex w-full max-w-content items-center gap-x-3 px-3 pt-3 max-mobile:flex-wrap max-mobile:gap-y-2 large:px-16 touch-device:px-4">
          {/* On phones the whole row scrolls, edge to edge, and the sort
              drops to its own line below. */}
          <ScrollArea
            enableShadows
            className="min-w-0 flex-1 max-mobile:-mx-3 max-mobile:basis-full"
          >
            <div className="flex w-max items-center gap-x-2 max-mobile:px-3">
              {hubLayout && filtersButton && (
                <>
                  <div className="shrink-0">{filtersButton}</div>
                  <span className="size-1 shrink-0 rounded-full bg-foreground-disabled" />
                </>
              )}
              <div>{quickFilters}</div>
            </div>
            <ScrollBar orientation="horizontal" className="h-1.5" />
          </ScrollArea>
          {showSort && (
            <div className="ml-auto flex shrink-0 items-center gap-x-1">
              {/* Meta rating sorts explain themselves, like the rating chip. */}
              {sortBy?.startsWith("meta-rating") && (
                <MetaRatingExplainer>
                  <span
                    aria-label={t("common:metaRatingExplanation")}
                    className="flex cursor-help text-foreground-muted hover:text-foreground"
                  >
                    <Info className="size-4" />
                  </span>
                </MetaRatingExplainer>
              )}
              <SortDropdown
                width={isTouchDevice ? 140 : 180}
                items={sortItems}
                selected={String(currentSortOption?.value ?? sortBy)}
                label={sortLabel}
                onChange={(value) => {
                  setSortBy(value as PlayersSortByType);
                  trackSort(value);
                }}
              />
            </div>
          )}
          {!hubLayout && viewToggle}
        </div>
      )}
      {chips && (
        <div className="mx-auto w-full max-w-content px-3 pt-3 large:px-16 touch-device:px-4">
          {chips}
        </div>
      )}
    </>
  );
};

export const PlayersToolbar = <T extends string>({
  searchTerm,
  setSearchTerm,
  handleToolbarAction,
  isMobile,
  hasFilters,
  sortBy,
  setSortBy,
  playerRole,
  selectedPlayerRoles,
  pageType,
  showPlayerRole = true,
  saving,
  currentEvolutionPlayer,
  isEvolutionsBuilderContext,
  includedEvolutionsCount = 0,
  activeFilters,
  onClearFilter,
  onClearFilterOption,
  onClearAllFilters,
  excludedPlayStyleChips,
  onClearExcludedPlayStyle,
  quickFilters,
  compareMode,
  viewToggle,
}: Props<T>) => {
  const { t } = useTranslation(["players", "common"]);

  const effectivePlayerRoleId =
    selectedPlayerRoles?.length === 1
      ? selectedPlayerRoles[0]?.id
      : playerRole?.id;

  const playerRoleLabel = useSelector(
    playerRoleLabelSelector({
      playerRoleId: effectivePlayerRoleId,
      appendPosition: true,
    }),
  );

  // Determine the label to display for the roles button
  const rolesButtonLabel = useMemo(() => {
    if (selectedPlayerRoles && selectedPlayerRoles.length > 1) {
      return null; // Will show "Roles" with count badge
    }
    return playerRoleLabel || t("playerRole.best");
  }, [selectedPlayerRoles, playerRoleLabel, t]);

  // sort: fire from the shared toolbar (single home for every context that
  // renders the sort dropdown). Branch the section/subsection off pageType.
  // SKIP for "my-evolutions" (sort-on-My-Evolutions is intentionally not
  // implemented) and for untracked contexts (playstyles-lab; builder hides the
  // dropdown).
  const trackSortForContext = (value: string) => {
    // The evo-builder player picker reuses pageType "players" but is the
    // Evolutions/Builder Overview surface — disambiguate via this flag.
    if (isEvolutionsBuilderContext) {
      evolutionsMixpanel.trackSort({ subsection: "Builder Overview", value });
      return;
    }
    switch (pageType) {
      case "players":
        playersMixpanel.trackSort({ subsection: "Overview", value });
        break;
      case "evolutions":
        evolutionsMixpanel.trackSort({ subsection: "Players", value });
        break;
      default:
        break;
    }
  };

  const sortOptions = useMemo<BaseDropdownOption[]>(() => {
    const options = isEvolutionsBuilderContext
      ? EVOS_BUILDER_SORT_OPTIONS
      : pageType === "players"
        ? PLAYERS_PAGE_SORT_OPTIONS
        : EVOLUTION_PLAYERS_SORT_OPTIONS;
    return options.map((o) => ({ label: t(o.labelKey), value: o.value }));
  }, [pageType, isEvolutionsBuilderContext, t]);

  const currentSortOption = useMemo(() => {
    if (!sortBy || !setSortBy) return null;
    return (
      sortOptions.find((option) => option.value === sortBy) || sortOptions[0]
    );
  }, [sortBy, setSortBy, sortOptions]);

  return (
    <div className="relative flex w-full flex-col">
      <NewPlayersToolbar
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        handleToolbarAction={handleToolbarAction}
        isMobile={isMobile}
        sortBy={sortBy}
        setSortBy={setSortBy}
        sortOptions={sortOptions}
        currentSortOption={currentSortOption}
        trackSort={trackSortForContext}
        pageType={pageType}
        showPlayerRole={showPlayerRole}
        rolesButtonLabel={rolesButtonLabel}
        pickedRoleLabel={effectivePlayerRoleId ? playerRoleLabel : undefined}
        selectedPlayerRoles={selectedPlayerRoles}
        saving={saving}
        currentEvolutionPlayer={currentEvolutionPlayer}
        isEvolutionsBuilderContext={isEvolutionsBuilderContext}
        includedEvolutionsCount={includedEvolutionsCount}
        quickFilters={quickFilters}
        compareMode={compareMode}
        viewToggle={viewToggle}
        chips={
          hasFilters && (
            <FiltersRow
              activeFilters={activeFilters}
              onClearFilter={onClearFilter}
              onClearFilterOption={onClearFilterOption}
              onClearAllFilters={onClearAllFilters}
              excludedPlayStyleChips={excludedPlayStyleChips}
              onClearExcludedPlayStyle={onClearExcludedPlayStyle}
            />
          )
        }
      />
    </div>
  );
};
